import { normalizeTerm } from './vocab';

interface Seg {
  id: string;
  idx: number;
  startMs: number;
  endMs: number;
  en: string;
  zh: string;
}

/** A study unit for 精听: one or more consecutive caption sentences. */
export interface StudyUnit {
  key: string;
  segmentIds: string[];
  startMs: number;
  endMs: number;
  en: string;
  zh: string;
}

const words = (s: string) => s.split(/\s+/).filter((w) => /[A-Za-z]/.test(w)).length;

/**
 * Captions are cut per sentence, so "OK." or "Right." become their own line.
 * For intensive listening, tiny sentences (< minWords or < minMs) are joined to a
 * neighbour, as long as the unit stays under maxMs / maxWords and no long pause is crossed.
 */
export function studyUnits(segments: Seg[], opts: { minWords?: number; minMs?: number; maxMs?: number; maxWords?: number; maxGapMs?: number } = {}): StudyUnit[] {
  const { minWords = 5, minMs = 1600, maxMs = 12_000, maxWords = 30, maxGapMs = 2000 } = opts;
  const small = (u: Seg[]) => words(u.map((s) => s.en).join(' ')) < minWords || u[u.length - 1]!.endMs - u[0]!.startMs < minMs;
  const fits = (a: Seg[], b: Seg[]) => {
    const all = [...a, ...b];
    return all[all.length - 1]!.endMs - all[0]!.startMs <= maxMs && words(all.map((s) => s.en).join(' ')) <= maxWords && b[0]!.startMs - a[a.length - 1]!.endMs <= maxGapMs;
  };
  const groups: Seg[][] = segments.map((s) => [s]);
  // Merge small groups forward (into the next) or, failing that, backward.
  for (let i = 0; i < groups.length; i++) {
    const g = groups[i]!;
    if (!small(g)) continue;
    const next = groups[i + 1];
    const prev = groups[i - 1];
    if (next && fits(g, next)) {
      groups.splice(i, 2, [...g, ...next]);
      i -= 1; // re-check the merged group
    } else if (prev && fits(prev, g)) {
      groups.splice(i - 1, 2, [...prev, ...g]);
      i -= 2;
    }
    if (i < -1) i = -1;
  }
  return groups.map((g) => ({
    key: g.map((s) => s.id).join('+'),
    segmentIds: g.map((s) => s.id),
    startMs: g[0]!.startMs,
    endMs: g[g.length - 1]!.endMs,
    en: g.map((s) => s.en).join(' '),
    zh: g.map((s) => s.zh).filter(Boolean).join(''),
  }));
}

const STOP = new Set(
  'the a an and or but so to of in on at for with from by as is are was were be been being am do does did have has had i you he she it we they me him her us them my your his its our their this that these those there here what which who whom whose when where why how not no yes ok okay right well oh just very really then than too also about into over out up down off can could will would shall should may might must let get got go going gone said say says like know think yeah hi hello one two'.split(
    ' ',
  ),
);

export interface ClozeBlank {
  /** Index into tokenize(en). */
  token: number;
  answer: string;
  options: string[];
}

/**
 * Pick 1–2 key words to blank out (preferring words from the episode's expressions,
 * then longer content words) and build 4 tap-to-fill options from the episode vocabulary.
 */
export function pickCloze(
  tokens: { text: string; word?: string }[],
  opts: { prefer?: string[]; pool: string[]; seed?: number; max?: number },
): ClozeBlank[] {
  const prefer = new Set((opts.prefer ?? []).flatMap((p) => normalizeTerm(p).split(' ')));
  const candidates = tokens
    .map((t, i) => ({ i, w: t.word ?? '' }))
    .filter(({ w }) => w.length >= 4 && !STOP.has(w) && !w.includes("'"));
  if (!candidates.length) return [];
  const score = (w: string) => (prefer.has(w) ? 100 : 0) + w.length;
  const max = opts.max ?? (tokens.filter((t) => t.word).length >= 12 ? 2 : 1);
  const chosen = [...candidates].sort((a, b) => score(b.w) - score(a.w) || a.i - b.i).slice(0, max).sort((a, b) => a.i - b.i);
  let seed = opts.seed ?? 1;
  const rand = () => ((seed = (seed * 9301 + 49297) % 233280) / 233280);
  const pool = [...new Set(opts.pool.map((w) => w.toLowerCase()).filter((w) => w.length >= 3 && !STOP.has(w)))];
  return chosen.map(({ i, w }) => {
    const near = pool.filter((p) => p !== w && Math.abs(p.length - w.length) <= 3);
    const distractors: string[] = [];
    while (distractors.length < 3 && near.length) distractors.push(near.splice(Math.floor(rand() * near.length), 1)[0]!);
    const options = [...distractors, w];
    for (let k = options.length - 1; k > 0; k--) {
      const j = Math.floor(rand() * (k + 1));
      [options[k], options[j]] = [options[j]!, options[k]!];
    }
    return { token: i, answer: w, options };
  });
}

/**
 * Five "精华句" for shadowing: sentences carrying the episode's expressions first,
 * then ones the learner found hard, then substantial mid-length sentences — in episode order.
 */
export function pickShadowing<T extends { id: string; en: string; startMs: number; endMs: number }>(segments: T[], expressionSegmentIds: string[], hardSegmentIds: string[], n = 5): T[] {
  const byId = new Map(segments.map((s) => [s.id, s]));
  const picked = new Map<string, T>();
  const add = (id: string) => {
    const s = byId.get(id);
    if (s && picked.size < n && words(s.en) >= 4) picked.set(id, s);
  };
  expressionSegmentIds.forEach(add);
  hardSegmentIds.forEach(add);
  if (picked.size < n) {
    const good = segments
      .filter((s) => !picked.has(s.id) && words(s.en) >= 7 && words(s.en) <= 18 && s.endMs - s.startMs <= 7000)
      .sort((a, b) => words(b.en) - words(a.en));
    for (const s of good) {
      if (picked.size >= n) break;
      picked.set(s.id, s);
    }
  }
  return [...picked.values()].sort((a, b) => a.startMs - b.startMs);
}
